/*
 * Delugs Strap Finder — Shared Rendering Engine
 * ------------------------------------------------------------------
 * One engine for the strap carousel mechanics shared across:
 *   - strap-model-page.liquid   (per-watch model landing pages)
 *   - mys-strap-finder.liquid   (homepage multi-watch picker)
 *   - strap-finder-lite.liquid  (lightweight per-watch embed)
 *
 * Each section keeps its own markup, CSS, tab UI, and data source.
 * It hands the engine a config + per-tab descriptors and calls loadTab().
 * The engine owns: product fetch + buckle-colour image matching,
 * carousel build + Flickity lifecycle, margin application (regular vs
 * integrated), watch-face overlay sizing, buy-button, and counter.
 *
 * Dependencies (already present in theme): jQuery, Flickity, imagesLoaded.
 *
 * Public API:
 *   var engine = window.StrapFinderEngine(config);
 *   engine.loadTab(tab);          // render a tab's straps
 *   engine.reconfigure(partial);  // update watch-level config (homepage switch)
 *   engine.getSelected();         // { title, handle } of centered card
 *   engine.destroy();             // tear down Flickity + handlers
 *
 * config = {
 *   $: jQuery,                          // jQuery instance
 *   container: Element|selector,        // carousel container
 *   classes: { cell, top, bottom },     // cell + strap img class names
 *   overlay: Element|selector|null,     // where the watch-face <img> is injected
 *   buyBtn, counter, nameEl, statusEl, compactBar: selector|null,
 *   // Section-specific rendering — provide callbacks to fully own the footer/nav
 *   // (homepage uses these for price + custom prev/next). If omitted, the engine
 *   // falls back to the selector-based defaults above (model-page / lite).
 *   onSelect: fn({title, handle}, tab) | null,
 *   onCounter: fn(current, total) | null,
 *   onRendered: fn($container) | null,   // e.g. wire custom prev/next buttons
 *   flickity: { prevNextButtons: bool }, // override Flickity options
 *   fetchMode: 'ajax' | 'lite',         // collection endpoint variant
 *   buckle: 'silver',
 *   cropMobile, cropDesktop,            // strap crop param strings
 *   faceCrop,                           // watch-face crop param string
 *   watchModel, watchModelShort,        // for alt text + model-specific filter
 *   lugWidth, watchFaceUrl, customUrl,
 *   excludeOtherModels: bool,           // drop "X for <other model>" straps
 *   margins: { regularTop, regularBottom, integrated: { top, bottom } | null },
 *   colourEncode: fn(str)->str | null,  // colour slug encoder (model-page chips)
 *   buyLabels: { product, custom },
 *   statusLabels: { product, custom },
 * }
 *
 * tab = {
 *   collection, material, colour,
 *   titleFilter, titleExclude,
 *   buyMode: 'product' | 'custom',
 *   isIntegrated: bool,
 *   onProductsParsed: fn(products, tab)->products | null,  // lite shared-collection filter
 * }
 */
(function () {
  'use strict';

  var DEFAULT_CROP_MOBILE = 'crop=region&crop_height=640&crop_left=335&crop_top=0&crop_width=520';
  var DEFAULT_CROP_DESKTOP = 'crop=region&crop_height=640&crop_left=195&crop_top=0&crop_width=800';
  var DEFAULT_FACE_CROP = 'crop=region&crop_height=1000&crop_left=100&crop_top=30&crop_width=800';

  function StrapFinderEngine(config) {
    if (!(this instanceof StrapFinderEngine)) return new StrapFinderEngine(config);

    var $ = config.$ || window.jQuery;
    var self = this;

    // ── Resolve config with defaults ──
    var classes = config.classes || {};
    var cellClass = classes.cell || 'columns';
    var topClass = classes.top || 'sf-top-strap';
    var bottomClass = classes.bottom || 'sf-bottom-strap';

    var cfg = {
      fetchMode: config.fetchMode || 'ajax',
      buckle: (config.buckle || 'silver').toLowerCase(),
      cropMobile: config.cropMobile || DEFAULT_CROP_MOBILE,
      cropDesktop: config.cropDesktop || DEFAULT_CROP_DESKTOP,
      faceCrop: config.faceCrop || DEFAULT_FACE_CROP,
      watchModel: config.watchModel || '',
      watchModelShort: config.watchModelShort || '',
      lugWidth: config.lugWidth || '',
      watchFaceUrl: config.watchFaceUrl || '',
      customUrl: config.customUrl || '/products/custom',
      excludeOtherModels: config.excludeOtherModels !== false, // default true
      margins: config.margins || { regularTop: 0, regularBottom: 0, integrated: null },
      colourEncode: config.colourEncode || null,
      buyLabels: config.buyLabels || { product: 'Buy Strap →', custom: 'Order Custom Strap →' },
      statusLabels: config.statusLabels || { product: 'Available', custom: 'Available as custom order' },
    };

    var onSelectCb = typeof config.onSelect === 'function' ? config.onSelect : null;
    var onCounterCb = typeof config.onCounter === 'function' ? config.onCounter : null;
    var onRenderedCb = typeof config.onRendered === 'function' ? config.onRendered : null;
    var flickityOpts = config.flickity || {};

    // Resolve DOM references (accept element or selector)
    function resolve(ref) {
      if (!ref) return null;
      if (ref.nodeType) return $(ref);
      return $(ref);
    }
    var $container = resolve(config.container);
    var $overlay = resolve(config.overlay);
    var $buyBtn = resolve(config.buyBtn);
    var $counter = resolve(config.counter);
    var $nameEl = resolve(config.nameEl);
    var $statusEl = resolve(config.statusEl);
    var $compactBar = resolve(config.compactBar);

    // Delugs: log strap buy-button clicks with watch context (fire-and-forget,
    // beacon survives the navigation to the PDP).
    if ($buyBtn && $buyBtn.on) {
      $buyBtn.off('click.sflog').on('click.sflog', function () {
        try {
          var sid = '';
          try { sid = localStorage.getItem('sf_sid') || ''; } catch (e0) {}
          if (!sid) { sid = 's' + Math.random().toString(36).slice(2) + Date.now().toString(36); try { localStorage.setItem('sf_sid', sid); } catch (e1) {} }
          var strapName = ''; try { strapName = $nameEl && $nameEl.text ? $nameEl.text() : ''; } catch (e2) {}
          var payload = JSON.stringify({
            eventType: 'strap_clicked',
            brand: (function(){ try { return localStorage.getItem('brand') || ''; } catch(e){ return ''; } })(),
            collection: (function(){ try { return localStorage.getItem('model') || ''; } catch(e){ return ''; } })(),
            reference: (function(){ try { return localStorage.getItem('reference') || ''; } catch(e){ return ''; } })(),
            strap: ((this.getAttribute && this.getAttribute('href')) || '') + '|' + strapName,
            sessionId: sid,
            pageType: 'landing',
            referrer: window.location.pathname
          });
          if (navigator.sendBeacon) navigator.sendBeacon('https://sf.delugs.com/api/selections', new Blob([payload], { type: 'text/plain;charset=UTF-8' }));
        } catch (err) {}
      });
    }

    // ── Per-instance state ──
    var loading = false;
    var origProductCount = 0;
    var fetchToken = 0;            // bump to invalidate in-flight fetches
    var currentTab = null;        // last loaded tab descriptor
    var changeHandler = null;     // named Flickity handlers for clean teardown
    var settleHandler = null;
    var reverseMode = false;      // loadWatches() sets true — margins/overlay are per-cell there

    // ── Image matching: two-pass buckle-colour, then generic fallback ──
    function fetchProducts(handle, material, colour, cb) {
      var token = fetchToken;
      var all = [], page = 1;
      var buckle = cfg.buckle;
      (function next() {
        var url;
        if (cfg.fetchMode === 'lite') {
          url = '/collections/' + handle + '?view=strap-lite&buckle=' + encodeURIComponent(buckle) + '&limit=50&page=' + page;
        } else {
          url = '/collections/' + handle + '?view=ajax&limit=50&page=' + page;
        }
        if (material) url += '&filter.p.m.custom.leather_pattern=' + encodeURIComponent(material);
        if (colour && cfg.colourEncode) url += '&filter.p.m.filter.colour=' + cfg.colourEncode(colour);
        $.ajax({ url: url, cache: true }).done(function (res) {
          if (token !== fetchToken) return; // stale — a newer load started
          var data; try { data = typeof res === 'string' ? JSON.parse(res) : res; } catch (e) { data = { products: [] }; }
          var prods = data.products || [];
          var topAlt = 'sf_wvtop_' + buckle, bottomAlt = 'sf_wvbottom_' + buckle;
          prods.forEach(function (p) {
            if (!p.variants || !p.variants.length) return;
            var top = null, bot = null;
            var media = p.media || [];
            // Pass 1: exact buckle colour — scan the WHOLE array first so a non-buckle
            // image appearing earlier (e.g. straps that lead with yellowgold) can't win.
            media.forEach(function (m) {
              if (!m.alt) return;
              if (!top && m.alt.indexOf(topAlt) !== -1) top = m;
              if (!bot && m.alt.indexOf(bottomAlt) !== -1) bot = m;
            });
            // Pass 2: generic fallback only if the buckle-specific image is missing entirely.
            if (!top || !bot) {
              media.forEach(function (m) {
                if (!m.alt) return;
                if (!top && m.alt.indexOf('sf_wvtop') !== -1) top = m;
                if (!bot && m.alt.indexOf('sf_wvbottom') !== -1) bot = m;
              });
            }
            if (top && bot && top.src && bot.src) all.push({ handle: p.handle, title: p.title, price: p.price, sf_top: top.src, sf_bottom: bot.src });
          });
          if (prods.length >= 50 && all.length < 50) { page++; next(); }
          else if (token === fetchToken) cb(all);
        }).fail(function () { if (token === fetchToken) cb(all); });
      })();
    }

    // ── Apply post-fetch filters (title, model-specific, custom hook) ──
    function applyFilters(products, tab) {
      if (tab.titleFilter) {
        products = products.filter(function (p) { return p.title && p.title.indexOf(tab.titleFilter) !== -1; });
      }
      if (tab.titleExclude) {
        products = products.filter(function (p) { return !p.title || p.title.indexOf(tab.titleExclude) === -1; });
      }
      if (cfg.excludeOtherModels && cfg.watchModel) {
        var pageModel = cfg.watchModel.toLowerCase();
        var pageModelShort = (cfg.watchModelShort || '').toLowerCase();
        products = products.filter(function (p) {
          if (!p.title) return true;
          var forIdx = p.title.toLowerCase().indexOf(' for ');
          if (forIdx === -1) return true; // not model-specific — keep
          var forModel = p.title.substring(forIdx + 5).toLowerCase();
          return pageModel.indexOf(forModel) !== -1 || (pageModelShort && (pageModelShort.indexOf(forModel) !== -1 || forModel.indexOf(pageModelShort) !== -1));
        });
      }
      if (typeof tab.onProductsParsed === 'function') {
        products = tab.onProductsParsed(products, tab) || products;
      }
      return products;
    }

    function applyCrop(url, crop) {
      if (!url) return '';
      var p = url.split('?v=');
      return p.length > 1 ? p[0] + '?' + crop + '&v=' + p[1] : url + '?' + crop;
    }

    // ── Build carousel cells + Flickity ──
    function renderCarousel(products) {
      if (!$container || !$container.length) return;
      $container.empty();
      if (!products.length) {
        $container.append('<div style="text-align:center;padding:60px 0;color:#999;font-size:14px;">No straps found for this filter.</div>');
        if ($counter) $counter.text('0 / 0');
        if ($compactBar) $compactBar.css('visibility', 'visible');
        return;
      }
      var isMobile = window.innerWidth <= 798;
      var crop = isMobile ? cfg.cropMobile : cfg.cropDesktop;
      var watchModel = cfg.watchModel;
      var html = '';
      products.forEach(function (p, i) {
        var altText = p.title + (watchModel ? ' on ' + watchModel : '');
        html += '<div class="' + cellClass + ' ' + i + '" product-title="' + escAttr(p.title) + '" product-handle="' + escAttr(p.handle) + '" data-title="' + escAttr(p.title) + '" data-handle="' + escAttr(p.handle) + '" data-price="' + (p.price || 0) + '">'
          + '<img class="' + topClass + ' no-blur lazyautosizes lazyloaded" src="' + applyCrop(p.sf_top, crop) + '" alt="' + escAttr(altText) + '" style="top:0px">'
          + '<img class="' + bottomClass + ' no-blur lazyautosizes lazyloaded" src="' + applyCrop(p.sf_bottom, crop) + '" alt="' + escAttr(altText) + '" style="top:0px">'
          + '</div>';
      });
      $container.append(html).addClass('strap-count-more');

      origProductCount = products.length;

      // Clone cells to fill carousel for seamless wrap-around (pre-Flickity: cells are direct children)
      var targetCount = Math.max((window.innerWidth <= 480 ? 3 : 7) * 3, 21);
      var $origItems = $container.children('.' + cellClass);
      if ($origItems.length > 0 && $origItems.length < targetCount) {
        while ($container.children('.' + cellClass).length < targetCount) {
          $origItems.clone().appendTo($container);
        }
      }

      window.imagesLoaded($container[0], function () {
        if ($.fn.flickity) {
          $container.flickity({
            cellAlign: 'center',
            contain: false,
            wrapAround: true,
            prevNextButtons: flickityOpts.prevNextButtons !== undefined ? flickityOpts.prevNextButtons : true,
            pageDots: false,
            draggable: true,
            resize: true,
            percentPosition: true,
          });
          $container.flickity('resize');
          changeHandler = function () { updateMargins(); updateOverlay(); updateSelected(); updateCounter(); };
          settleHandler = function () { updateMargins(); updateOverlay(); };
          $container.on('change.flickity', changeHandler);
          $container.on('settle.flickity', settleHandler);
        } else {
          // No Flickity — mark first cell selected so margins/overlay apply
          $container.find('.' + cellClass).first().addClass('is-selected');
        }
        updateMargins(); updateOverlay(); updateSelected(); updateCounter();
        if ($compactBar) $compactBar.css('visibility', 'visible');
        if (onRenderedCb) onRenderedCb($container);
      });
    }

    // ── Margins: applied to the selected (centered) cell only ──
    function updateMargins() {
      if (reverseMode) return; // reverse cells carry their own baked margins
      var m = cfg.margins;
      var useIntegrated = currentTab && currentTab.isIntegrated && m.integrated;
      var topM = useIntegrated ? m.integrated.top : m.regularTop;
      var botM = useIntegrated ? m.integrated.bottom : m.regularBottom;
      var $cells = $container.find('.' + cellClass);
      $cells.find('img.' + topClass).css('top', '0px');
      $cells.find('img.' + bottomClass).css('top', '0px');
      var $sel = $cells.filter('.is-selected');
      if ($sel.length) {
        $sel.find('img.' + topClass).css('top', (topM || 0) + 'px');
        $sel.find('img.' + bottomClass).css('top', (botM || 0) + 'px');
      }
    }

    // ── Watch-face overlay: sized to the selected strap stack ──
    function updateOverlay() {
      if (reverseMode) return; // reverse mode has no shared overlay — watch is per-cell
      if (!$overlay || !$overlay.length) return;
      setTimeout(function () {
        var $sel = $container.find('.' + cellClass + '.is-selected');
        if (!$sel.length) $sel = $container.find('.' + cellClass).first();
        var topH = $sel.find('img.' + topClass).height() || 0;
        var botH = $sel.find('img.' + bottomClass).height() || 0;
        var totalH = topH + botH;
        var face = cfg.watchFaceUrl;
        if (totalH > 0 && face) {
          var url;
          if (face.indexOf('?v=') !== -1) url = face.replace(/\?v=/, '?' + cfg.faceCrop + '&v=');
          else if (face.indexOf('?') !== -1) url = face + '&' + cfg.faceCrop;
          else url = face + '?' + cfg.faceCrop;
          $overlay.css('position', 'absolute').html('<img class="watch-face" src="' + url + '" style="height:' + totalH + 'px!important">');
        }
      }, 100);
    }

    // ── Buy button + selected name/status ──
    // If config.onSelect is provided, the section owns this entirely (price, custom
    // meta format, etc.). Otherwise use the built-in selector-based defaults.
    function updateSelected() {
      var $sel = $container.find('.' + cellClass + '.is-selected');
      var sel = {
        title: $sel.attr('product-title') || $sel.attr('data-title') || '',
        handle: $sel.attr('product-handle') || $sel.attr('data-handle') || '',
        price: parseInt($sel.attr('data-price'), 10) || 0,
      };
      if (onSelectCb) { onSelectCb(sel, currentTab); return; }
      if ($nameEl) $nameEl.text(sel.title);
      var isCustom = currentTab && currentTab.buyMode === 'custom';
      if (isCustom) {
        if ($statusEl) $statusEl.text(sel.title ? cfg.statusLabels.custom : '').css('color', '#888888');
        if ($buyBtn) $buyBtn.text(cfg.buyLabels.custom).attr('href', cfg.customUrl);
      } else {
        if ($statusEl) $statusEl.text(sel.title ? cfg.statusLabels.product : '');
        if ($buyBtn) $buyBtn.text(cfg.buyLabels.product).attr('href', '/products/' + sel.handle + (cfg.lugWidth ? '?lugwidth:' + cfg.lugWidth : ''));
      }
    }

    function updateCounter() {
      var current = 1;
      var f = $container.data('flickity');
      if (f && origProductCount) current = (f.selectedIndex % origProductCount) + 1;
      if (onCounterCb) { onCounterCb(origProductCount ? current : 0, origProductCount); return; }
      if ($counter && origProductCount) $counter.text(current + ' / ' + origProductCount);
    }

    function escAttr(s) {
      return String(s == null ? '' : s).replace(/"/g, '&quot;');
    }

    // ── Public methods ──
    self.loadTab = function (tab) {
      if (!tab || !tab.collection) return;
      reverseMode = false;
      currentTab = tab;
      fetchToken++; // invalidate any in-flight fetch
      loading = true;
      if ($container.data('flickity')) {
        $container.off('change.flickity', changeHandler);
        $container.off('settle.flickity', settleHandler);
        $container.flickity('destroy');
      }
      $container.empty().append('<div class="sfl-loading-overlay"><div class="sfl-spinner"></div></div>');
      if ($compactBar) $compactBar.css('visibility', 'hidden');
      if ($overlay) $overlay.empty();
      fetchProducts(tab.collection, tab.material || '', tab.colour || '', function (products) {
        products = applyFilters(products, tab);
        renderCarousel(products);
        loading = false;
      });
    };

    // ── Reverse mode (strap PDP, roadmap #30): the STRAP is fixed, cells are WATCHES ──
    // Each cell is a full mini-composite: the strap halves (flush, with that
    // watch's margin nudges baked in) plus the watch head sized to the strap
    // stack and centred on top — the same geometry the forward carousel gives
    // its selected cell, repeated per watch. Same cell class, crops, clone-fill
    // and Flickity options as renderCarousel, so strap-finder-surface.css
    // styles it identically (cell widths, 0.5/1 opacity, buttons).
    // items: [{ head, top, bottom, topMargin, bottomMargin, title, handle, alt }]
    // NOTE: pass clean image URLs (base + ?v= only) — crops are applied here.
    self.loadWatches = function (items) {
      if (!$container || !$container.length) return;
      reverseMode = true;
      currentTab = null;
      fetchToken++;
      if ($container.data('flickity')) {
        $container.off('change.flickity', changeHandler);
        $container.off('settle.flickity', settleHandler);
        $container.flickity('destroy');
      }
      $container.empty();
      if (!items || !items.length) { if ($counter) $counter.text('0 / 0'); return; }
      // ALWAYS the desktop crop, even on mobile: every reverse cell is a full
      // watch+strap composite, and only the desktop framing keeps the case
      // covering the strap's middle section at head-height = stack. (The
      // mobile crop is a taller slice meant for the forward finder's
      // strap-only side cells — with a watch on top it either overlaps the
      // neighbours at 100% height or exposes the strap lining at less.)
      var crop = cfg.cropDesktop;
      var html = '';
      items.forEach(function (w, i) {
        html += '<div class="' + cellClass + ' ' + i + ' sf-reverse-cell" product-title="' + escAttr(w.title) + '" product-handle="' + escAttr(w.handle) + '" data-title="' + escAttr(w.title) + '" data-handle="' + escAttr(w.handle) + '">'
          + '<img class="' + topClass + ' no-blur lazyautosizes lazyloaded" src="' + applyCrop(w.top, crop) + '" alt="" style="top:' + (w.topMargin || 0) + 'px">'
          + '<img class="' + bottomClass + ' no-blur lazyautosizes lazyloaded" src="' + applyCrop(w.bottom, crop) + '" alt="" style="top:' + (w.bottomMargin || 0) + 'px">'
          + '<img class="sf-reverse-watch" src="' + applyCrop(w.head, cfg.faceCrop) + '" alt="' + escAttr(w.alt || w.title) + '">'
          + '</div>';
      });
      $container.append(html).addClass('strap-count-more');
      origProductCount = items.length;
      // Clone-fill + wraparound ONLY with enough distinct watches — cloning a
      // 1-4 watch list repeats the same watch across the strip (looks broken,
      // Ken Jul 3). Small lists render finite: no clones, no wrap; a lone
      // watch also gets no arrows and no drag.
      var effWrap = items.length >= 5;
      if (effWrap) {
        var targetCount = Math.max((window.innerWidth <= 480 ? 3 : 7) * 3, 21);
        var $origItems = $container.children('.' + cellClass);
        if ($origItems.length > 0 && $origItems.length < targetCount) {
          while ($container.children('.' + cellClass).length < targetCount) {
            $origItems.clone().appendTo($container);
          }
        }
      }
      window.imagesLoaded($container[0], function () {
        if ($.fn.flickity) {
          $container.flickity({
            cellAlign: 'center',
            contain: false,
            wrapAround: effWrap,
            prevNextButtons: (flickityOpts.prevNextButtons !== undefined ? flickityOpts.prevNextButtons : true) && items.length > 1,
            pageDots: false,
            draggable: items.length > 1,
            resize: true,
            percentPosition: true,
          });
          $container.flickity('resize');
          // Margins are baked per-cell; only selection + counter update on change.
          changeHandler = function () { updateSelected(); updateCounter(); };
          settleHandler = function () { updateSelected(); updateCounter(); };
          $container.on('change.flickity', changeHandler);
          $container.on('settle.flickity', settleHandler);
        } else {
          $container.find('.' + cellClass).first().addClass('is-selected');
        }
        updateSelected(); updateCounter();
        if ($compactBar) $compactBar.css('visibility', 'visible');
        if (onRenderedCb) onRenderedCb($container);
      });
    };

    self.reconfigure = function (partial) {
      Object.keys(partial || {}).forEach(function (k) {
        if (k === 'margins') { cfg.margins = partial.margins; return; }
        if (k === 'buckle') { cfg.buckle = (partial.buckle || 'silver').toLowerCase(); return; }
        if (cfg.hasOwnProperty(k)) cfg[k] = partial[k];
      });
    };

    self.getSelected = function () {
      var $sel = $container.find('.' + cellClass + '.is-selected');
      return { title: $sel.attr('product-title') || $sel.attr('data-title') || '', handle: $sel.attr('product-handle') || $sel.attr('data-handle') || '' };
    };

    self.refresh = function () { updateMargins(); updateOverlay(); updateSelected(); updateCounter(); };

    self.destroy = function () {
      fetchToken++;
      if ($container && $container.data('flickity')) {
        $container.off('change.flickity', changeHandler);
        $container.off('settle.flickity', settleHandler);
        $container.flickity('destroy');
      }
      currentTab = null;
    };
  }

  window.StrapFinderEngine = StrapFinderEngine;
})();
